import { inject, Injectable } from '@angular/core';
import { NgxAimService } from '../ngx-aim.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AppStateService } from './app-state.service';
import { AimBaseResponse, ReserveStation, Station, StationGroup, StationTime } from '../models';
import { StationDurationEnum } from '../enums';

const STATION_GROUP_KEY = 'station-group';
const STATION_DATE_KEY = 'station-date';
const STATION_DURATION_KEY = 'station-duration';
const STATION_STATION_KEY = 'station-station';
const STATION_TIME_KEY = 'station-time';

@Injectable({
  providedIn: 'root'
})
export class StationsService {
  ngxAimService = inject(NgxAimService);
  http = inject(HttpClient);
  appStateService = inject(AppStateService);

  private baseUrl = `${this.ngxAimService.getApiUrl()}/stations`;

  private selectedGroup: StationGroup | null = null;
  private selectedDate: Date | null = null;
  private selectedDuration: StationDurationEnum | null = null;
  private selectedStation: Station | null = null;
  private selectedTime: StationTime | null = null;
  selectedAmountOfGuests = 0;
  Notes = '';
  allowedGuetsMessage = '';

  constructor() {
    if (this.appStateService.isBrowser) {
      this.setGroupFromLocalStorage();
      this.setDateFromLocalStorage();
      this.setDurationFromLocalStorage();
      this.setStationFromLocalStorage();
      this.setTimeFromLocalStorage();
    }
  }

  findAllStationGroups(LocationId: number): Observable<StationGroup[]> {
    return this.http.get<StationGroup[]>(`${this.baseUrl}/groups/${LocationId}`).pipe(map(groups => {
      groups.forEach((group) => {
        const disabledDates: Date[] = [];
        group.diabledDates.forEach((date) => {
          disabledDates.push(new Date(date));
        });
        group.minDate = new Date(group.minDate);
        group.maxDate = new Date(group.maxDate);
        group.diabledDates = disabledDates;
      });
      return groups;
    }));
  }

  findAllStations(GroupId: number): Observable<Station[]> {
    return this.http.get<Station[]>(`${this.baseUrl}/group/${GroupId}`, {
      params: new HttpParams()
        .set('date', this.selectedDate?.toISOString() || '')
        .set('duration', this.selectedDuration || '')
    });
  }

  completeReservation(): Observable<AimBaseResponse> {
    const dto: ReserveStation = {
      StationId: this.selectedStation!.Id,
      Start: this.selectedTime!.time,
      Length: this.selectedDuration!,
      Guests: this.selectedAmountOfGuests,
      Notes: this.Notes,
    }

    return this.http.post<AimBaseResponse>(`${this.baseUrl}/reserve`, dto)
  }

  setSelectedGroup(group: StationGroup) {
    this.selectedGroup = group;
    localStorage.setItem(STATION_GROUP_KEY, JSON.stringify(group));
  }

  getSelectedGroup() {
    return this.selectedGroup;
  }

  private setGroupFromLocalStorage() {
    const selectedGroup = localStorage.getItem(STATION_GROUP_KEY);
    if (selectedGroup) {
      const group = JSON.parse(selectedGroup) as StationGroup;
      const disabledDates: Date[] = [];
      group.diabledDates.forEach((date) => {
        disabledDates.push(new Date(date));
      });
      group.minDate = new Date(group.minDate);
      group.maxDate = new Date(group.maxDate);
      group.diabledDates = disabledDates;
      this.setSelectedGroup(group);
    }
  }

  setSelectedDate(date: Date) {
    this.selectedDate = date;
    localStorage.setItem(STATION_DATE_KEY, date.toISOString());
  }

  getSelectedDate() {
    return this.selectedDate;
  }

  private setDateFromLocalStorage() {
    const dateString = localStorage.getItem(STATION_DATE_KEY);
    if (dateString) {
      this.setSelectedDate(new Date(dateString))
    }
  }

  setSelectedDuration(duration: StationDurationEnum) {
    this.selectedDuration = duration;
    localStorage.setItem(STATION_DURATION_KEY, `${duration}`);
  }

  getSelectedDuration() {
    return this.selectedDuration;
  }

  private setDurationFromLocalStorage() {
    const durationString = localStorage.getItem(STATION_DURATION_KEY);
    if (durationString) {
      this.setSelectedDuration(+durationString)
    }
  }

  setSelectedStation(station: Station) {
    this.selectedStation = station;
    localStorage.setItem(STATION_STATION_KEY, JSON.stringify(this.selectedStation));
  }

  getSelectedStation() {
    return this.selectedStation;
  }

  private setStationFromLocalStorage() {
    const stationString = localStorage.getItem(STATION_STATION_KEY);
    if (stationString) {
      this.setSelectedStation(JSON.parse(stationString));
    }
  }

  setSelectedTime(time: StationTime) {
    this.selectedTime = time;
    localStorage.setItem(STATION_TIME_KEY, JSON.stringify(time));
  }

  getSelectedTime() {
    return this.selectedTime;
  }

  private setTimeFromLocalStorage() {
    const time = localStorage.getItem(STATION_TIME_KEY);
    if (time) {
      this.setSelectedTime(JSON.parse(time));
    }
  }

  reset() {
    this.selectedGroup = null;
    this.selectedDate = null;
    this.selectedDuration = null;
    this.selectedStation = null;
    this.selectedTime = null;
    this.selectedAmountOfGuests = 0;
    this.Notes = '';
    this.allowedGuetsMessage = '';
  }
}
